import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { generatePath } from 'react-router';

import { translate } from 'services/i18nService';
import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import CookieStorageService from 'services/CookieStorageService';
import UserStore from 'stores/UserStore';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { ENUM_USER_TYPE } from 'enum/profile';
import { privateRoutes } from 'enum/routeDefinitions';
import { DAY_AS_MILLISECONDS } from 'enum/timeFormat';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import { resolveURLPath } from 'utils/urlHelpers';

import styles from './SmsModal.css';


const dismiss2FA = async(username) => {
  const baseUrl = ConfigurationService.get(ConfigurationKeys.BASE_URL);
  const endPoint = 'rest/v1.0/twoFaOptions/dismiss';

  const url = new URL(baseUrl);
  url.pathname = resolveURLPath(url.pathname, endPoint);

  const payload = new URLSearchParams();
  payload.append('username', username);
  payload.append('value', true);

  const headers = { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' };

  try {
    await ApiService.post(url.toString(), { data: payload.toString(), headers });
  } catch (err) {
    // Nothing to do
  }

  const expires = new Date(Date.now() + (3 * DAY_AS_MILLISECONDS));
  CookieStorageService.set(`SMS-${username}`, 'TWO_FA_INFORM_MODAL', { path: '/', expires });
};

const SmsModal = () => {
  const {
    accessLevel,
    accountType,
    twoFaDismissed,
    twoFaEnforced,
    username,
  } = UserStore.user;

  const isAdmin = accessLevel >= ACCESS_LEVEL_NUM.USER_MODERATOR;
  const isEnforced = twoFaEnforced || isAdmin;
  const isDismissed = twoFaDismissed || CookieStorageService.get(`SMS-${username}`);

  const [isOpen, setIsOpen] = useState(isEnforced || !isDismissed);

  const path = useMemo(() => {
    if (accountType === ENUM_USER_TYPE.STUDIO_OWNER) {
      return generatePath(privateRoutes.studio.settings.security, { username });
    }

    return generatePath(privateRoutes.settings.account.security, { username });
  }, [accountType, username]);

  const onCloseModal = useCallback(async() => {
    await dismiss2FA(username);

    setIsOpen(false);
  }, [username]);

  return (
    <Modal
      className={styles.modal}
      close={onCloseModal}
      disableBackdropClick={isEnforced}
      show={isOpen}
      showClose={!isEnforced}
      size="medium"
    >
      <h3>
        {translate('profile.manage.twoFa')}
      </h3>

      <p>
        {translate('profile.profile.manage.twofa.modal.content')}
      </p>

      <Button colour="orange" link={path}>
        {translate('profile.profile.manage.twofa.set.up')}
      </Button>

      <Button
        className={styles.dismissButton}
        colour="white"
        disabled={isEnforced}
        label={translate('profile.profile.manage.twofa.dismissed')}
        onClick={onCloseModal}
      />
    </Modal>
  );
};

export default observer(SmsModal);
